import { useMobile } from '@act/utilities/MobileContext';
import { HeaderAccent } from '../../display/HeaderAccent/HeaderAccent';
import { AppHeaderLogo } from '../../navigation/AppHeaderLogo';
import {
  SideMenuCompactProvider,
  useDisplayModeContext,
} from '../../theme/display';
import {
  COMPACT_GRID_WIDTH,
  EXPANDED_GRID_WIDTH,
  HEADER_HEIGHT,
} from '@act/react-components/app/constants';
import { Box, useTheme } from '@mui/material';
import * as m from 'motion/react-m';
import { ReactElement, ReactNode } from 'react';
import ViewAsBanner from 'react-components/header/ViewAsBanner';
import SideMenuDrawer from 'react-components/SideMenuDrawer/SideMenuDrawer';

export interface AppShellGridProps {
  headerContent?: ReactElement;
  mainContent?: ReactNode;
  sideMenuContent?: ReactElement;
  hasContinuousHeader?: boolean;
  isOnlyContentPage?: boolean;
  /** When true, the side menu always uses compact layout regardless of displayMode context */
  sideMenuAlwaysCompact?: boolean;
  hideHeaderAccent?: boolean;
  /**
   * Removes the default 16px inset around the main outlet so page content can span
   * edge-to-edge (page background still shows in the main grid area). Header accent
   * and View-as banner are unchanged.
   */
  fullBleedMain?: boolean;
}

/**
 * Full-app CSS grid shell (header, optional branding, side menu, main). Lives under
 * `src/stories/` so layout can use MUI `sx` per **react-component.mdc** Storybook exception.
 */
export const AppShellGrid = ({
  headerContent,
  mainContent,
  sideMenuContent,
  hasContinuousHeader = false,
  isOnlyContentPage = false,
  sideMenuAlwaysCompact = true,
  hideHeaderAccent = false,
  fullBleedMain = false,
}: AppShellGridProps) => {
  const theme = useTheme();
  const { isMobile } = useMobile();
  const { displayMode, setIsAnimating } = useDisplayModeContext();
  const isCompact = sideMenuAlwaysCompact || displayMode === 'compact';

  const gridTemplateAreas = hasContinuousHeader
    ? '"header header" "sidemenu main"'
    : '"branding header" "sidemenu main"';

  const mobileGridTemplateAreas = hasContinuousHeader
    ? '"header header" "main main"'
    : '"header header" "main main"';

  const showSideMenu = !isMobile && !isOnlyContentPage;

  return (
    <>
      {isMobile && !isOnlyContentPage && (
        <SideMenuDrawer sideMenuContent={sideMenuContent} />
      )}

      <Box
        display="grid"
        gridTemplateAreas={
          isMobile || isOnlyContentPage
            ? mobileGridTemplateAreas
            : gridTemplateAreas
        }
        gridTemplateColumns={
          isMobile || isOnlyContentPage
            ? '1fr'
            : isCompact
              ? `${COMPACT_GRID_WIDTH} 1fr`
              : `${EXPANDED_GRID_WIDTH} 1fr`
        }
        gridTemplateRows="auto 1fr"
        height="100%"
        overflow="hidden"
        paddingBottom={
          isMobile
            ? ['constant(safe-area-inset-bottom)', 'env(safe-area-inset-bottom)']
            : 0
        }
        sx={{ transition: 'grid-template-columns 0.5s ease-in-out' }}
      >
        {hasContinuousHeader ? (
          <Box
            gridArea="header"
            overflow="hidden"
            height={HEADER_HEIGHT}
          >
            <AppHeaderLogo disableAnimations />
            {headerContent}
          </Box>
        ) : (
          <>
            {showSideMenu && (
              <Box
                gridArea="branding"
                display="grid"
                borderRight={`1px solid ${theme.palette.strokePrimary}`}
                borderBottom={`1px solid ${theme.palette.strokePrimary}`}
                sx={{ placeItems: 'center' }}
              >
                {sideMenuAlwaysCompact ? (
                  <SideMenuCompactProvider>
                    <AppHeaderLogo />
                  </SideMenuCompactProvider>
                ) : (
                  <AppHeaderLogo />
                )}
              </Box>
            )}
            {headerContent && (
              <Box
                gridArea="header"
                overflow="hidden"
                height={HEADER_HEIGHT}
              >
                {headerContent}
              </Box>
            )}
          </>
        )}

        {sideMenuContent && showSideMenu && (
          <Box
            component={m.div}
            animate={{
              width: isCompact ? COMPACT_GRID_WIDTH : EXPANDED_GRID_WIDTH,
            }}
            transition={{
              type: 'tween',
              duration: 0.5,
            }}
            gridArea="sidemenu"
            overflow="visible"
            minHeight={0}
            onAnimationStart={() => {
              if (setIsAnimating) setIsAnimating(true);
            }}
            onAnimationComplete={() => {
              if (setIsAnimating) setIsAnimating(false);
            }}
          >
            {sideMenuAlwaysCompact ? (
              <SideMenuCompactProvider>
                {sideMenuContent}
              </SideMenuCompactProvider>
            ) : (
              sideMenuContent
            )}
          </Box>
        )}
        {mainContent && (
          <Box
            gridArea="main"
            overflow={isMobile ? 'auto' : 'hidden'}
            minHeight={0}
            display="flex"
            flexDirection="column"
            sx={{ backgroundColor: theme.palette.neutralLight.main }}
          >
            {!hideHeaderAccent && <HeaderAccent />}
            <ViewAsBanner />
            <Box
              padding={hideHeaderAccent || fullBleedMain ? 0 : '16px'}
              flex={1}
              overflow={isMobile ? 'auto' : 'hidden'}
            >
              {mainContent}
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default AppShellGrid;
